/**
 * ── DOES THE PICKED PHOTO ACTUALLY FIT? (audit row 21) ────────────────────────
 * Port of the monolith's `__imgDimCheck` verdict (index.html L12796–12801).
 *
 * The block DECLARES its image size (`width`/`height` on the guardrail field), so
 * the panel can say — before anyone sends — that the photo a division picked out
 * of Bynder is the wrong shape or too small for the slot. It never blocks the
 * pick; SFMC will happily scale it, badly, and the point is to say so.
 *
 * THREE RULES, AND EACH IS DELIBERATE:
 *   · SHAPE FIRST, with a 2% tolerance. 600×350 and 1200×700 are the same photo
 *     at two sizes; 600×350 and 600×400 are not.
 *   · SAME SHAPE OR LARGER PASSES. A 2× retina asset is the RIGHT answer, not a
 *     warning — only `w < needW` is soft.
 *   · CROSS-ORIGIN SAFE. `naturalWidth`/`naturalHeight` are readable on any
 *     loaded image; nothing here touches a canvas. That matters because Bynder's
 *     CDN sends no CORS headers, which is the same reason overlay BAKING was
 *     removed from ECH image blocks (monolith L13438–13445) and must not come
 *     back — the canvas taints and every bake fails.
 */

import { isBynderTransform } from "./bynderCrop";

export interface DimVerdict {
  /** "" = the photo fits */
  cls: "" | "warn" | "bad";
  msg: string;
}

export const DIM_OK: DimVerdict = { cls: "", msg: "" };

/** the tolerance on the aspect ratio (monolith `off > 0.02`) */
export const DIM_TOL = 0.02;

/**
 * monolith L12796–12801, verbatim in its wording — the × is U+00D7 and the dash
 * is an em dash, because these strings are read next to the `imgc-dim` chip.
 */
export function imgDimVerdict(w: number, h: number, needW: number, needH: number): DimVerdict {
  if (!w || !h || !needW || !needH) return DIM_OK;
  const want = needW / needH;
  const got = w / h;
  const off = Math.abs(got - want) / want;
  if (off > DIM_TOL) {
    return { cls: "bad", msg: "Wrong shape — this photo is " + w + " × " + h + ", the block needs " + needW + " × " + needH + "." };
  }
  if (w < needW) {
    return { cls: "warn", msg: "Only " + w + " × " + h + " — smaller than the " + needW + " × " + needH + " slot, so it will look soft." };
  }
  return DIM_OK;
}

/** the chip text on the photo (monolith `_dim`, L12822) */
export function imgDimLabel(w?: number, h?: number): string {
  return w && h ? w + " × " + h : "";
}

/* ── AN EMPTY SINGLE IMAGE IS HIDDEN, NEVER STOOD IN FOR (2026-08-20) ──────────
   The Featured Plan blocks render `src="{{PlanImage}}"` off `plan.self.image`, and
   that source resolves to "" for any community whose SFMC pack has no plans — so
   the block shipped a literal `<img src="">`: a broken-image icon in the 45% column
   of a live send. The existing empty-image guard, `echAmen.amenTrimHtml`, is gated
   on the block declaring `icon<N>` fields (echRegistry's renderer), so it has never
   run here. This is the non-grid, single-image case.

   WHY HIDING BEATS A PLACEHOLDER, which was the other option:
    · There is no approved "no photo yet" asset. Substituting anything means either
      inventing a hosted URL — which is how a fabricated media link ends up in a
      customer email — or reusing the block's own shipped demo photo, which is a
      REAL Lennar floorplan. That ships a picture of the wrong home next to this
      plan's name, square footage and price. A missing photo reads as a missing
      photo; a wrong photo reads as the truth.
    · The requester can still fix it: the panel's Bynder picker is right there, and
      an absent image is the honest prompt to use it. A placeholder hides the gap.
    · It matches what the sibling guard already decided for the same reason — an
      unused amenity slot's cell is REMOVED, not filled, because "an empty slot
      would render a broken image" (echAmen.ts).

   The removal peels only scaffolding the image was the sole content of — the `<a>`
   wrapping it, the `<div>` wrapping that, and the `<td>` that was the image column —
   and stops at the block's tables. Two hard stops: a row is never left with no
   cells (an empty `<tr>` renders unpredictably), and a px width is never rewritten
   into a percentage (amenTrimHtml learned that one the hard way — a % only means
   what you intend inside a table whose own width resolves).

   DOM parsing and the same fallback posture as amenTrimHtml: no `DOMParser` (a bare
   Node parity run) returns the html untouched rather than throwing, and markup with
   no empty `src` is returned byte for byte so an unaffected block still exports the
   bytes Content Builder holds. */

/** wrappers that exist only to hold content, so an empty one is not markup */
const PEELABLE = /^(A|DIV|SPAN|P|TD)$/;

function isEmptyEl(el: Element): boolean {
  if (el.children.length) return false;
  /* &nbsp; is a spacer, not content — the Featured blocks use one as a shim */
  return !String(el.textContent || "").replace(/\u00a0/g, " ").trim();
}

/**
 * The surviving cells take the removed one's width back — PERCENTAGES ONLY.
 * A single `<td width="55%">` left in a `width="100%"` table renders full width in
 * Gmail and Apple Mail but genuinely 55% in Outlook's Word engine, so leaving it
 * would blank 45% of the block in the client that matters most.
 */
function reflowPct(row: Element): void {
  const cells: Element[] = Array.prototype.slice.call(row.children);
  if (!cells.length) return;
  const pct = (100 / cells.length).toFixed(4).replace(/\.?0+$/, "");
  cells.forEach((td) => {
    const w = String(td.getAttribute("width") || "");
    const st = String(td.getAttribute("style") || "");
    if (/^\s*\d+(\.\d+)?\s*(px)?\s*$/.test(w) || /width\s*:\s*[\d.]+px/i.test(st)) return;
    if (/%\s*$/.test(w)) td.setAttribute("width", pct + "%");
    if (/width\s*:\s*[\d.]+%/i.test(st)) td.setAttribute("style", st.replace(/width\s*:\s*[\d.]+%/i, "width: " + pct + "%"));
  });
}

/** remove every `<img src="">` and the wrappers it was the only content of */
export function dropEmptyImage(html: string): string {
  try {
    if (!/<img[^>]*src=""/.test(html)) return html;
    if (typeof DOMParser === "undefined") return html;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const imgs: Element[] = Array.prototype.slice.call(doc.querySelectorAll('img[src=""]'));
    if (!imgs.length) return html;
    let touched = false;
    for (const img of imgs) {
      let node: Element | null = img;
      let clearedRow: Element | null = null;
      while (node) {
        const parent: Element | null = node.parentElement;
        if (node.tagName.toUpperCase() === "TD") {
          /* never leave a row with no cells — stop and keep this one empty */
          if (!parent || parent.children.length < 2) break;
          clearedRow = parent;
        }
        parent?.removeChild(node);
        touched = true;
        if (clearedRow || !parent) break;
        if (!PEELABLE.test(parent.tagName.toUpperCase()) || !isEmptyEl(parent)) break;
        node = parent;
      }
      if (clearedRow) reflowPct(clearedRow);
    }
    if (!touched) return html;
    return doc.body.innerHTML;
  } catch {
    /* a broken image is better than no canvas */
    return html;
  }
}

/* ── A FEED PHOTO IS NEVER STRETCHED INTO THE CARD (2026-09-18) ────────────────────
   Jack, on a five-card Featured listing: "some of the images are stretched and not
   sized right." Measured on emailbuilder.lennar.com: the four cards drew lennar.com
   feed photos of 1433×803, 2160×800, 955×535 and 2070×900 into the block's
   `width="270" height="180"` + `height: 180px; width: 270px` — every one of them
   16:9 or wider, forced to 3:2, so each home was pulled tall.

   A Bynder pick is cut to exactly 270×180 by its transform URL, so the pin is right for
   it and it is left alone. A feed photo cannot be cut: `cdn.lennar.com/api/images` has
   no CORS (a canvas bake taints) and its only size parameter, `w=`, snaps to preset
   widths (576 / 738 / 900 / 1200) while keeping the photo's own shape — no crop. The
   uniform-crop path for feed photos is the backend proxy + `/sfmc-image` host (pending
   on the gateway); until it lands, the honest rendering is the photo at its own shape:

     · the img loses its pinned height — `width="270"` stays, `height: auto` — so it
       scales like the block's own phone rule already does (`width:100%; height:auto`);
     · its cell is left exactly as Layne authored it (valign top), so the photo sits
       level with the plan name on every card, Bynder-cut or feed, and the copy column
       (name + three stats + buttons, ~210px) still sets the card's height.

   Rewrites the ONE `<img>` whose src is the field's value — nothing else in the markup
   moves, so an untouched block ships byte for byte. Gated on
   a Featured card (a declared `PlanName` beside an image field that declares a size);
   the Main Image pins its height on purpose (Layne's height dropdown) and is not this. */

/** the fields that make a block a Featured card, beside its image */
const FEATURED_CARD_MARK = "PlanName";

function attrEsc(v: string): string {
  return v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function unpinFeedPhoto(
  html: string,
  d: Record<string, unknown>,
  declared: Record<string, { type?: string; width?: number; height?: number }>,
): string {
  try {
    if (!declared[FEATURED_CARD_MARK]) return html;
    let out = html;
    Object.keys(declared).forEach((k) => {
      const f = declared[k] || {};
      if (!/^image$/i.test(String(f.type || "")) || !f.width || !f.height) return;
      const v = String(d[k] == null ? "" : d[k]).trim();
      if (!v || /^data:/i.test(v) || isBynderTransform(v)) return;
      const srcs = [v, attrEsc(v)].filter((s, i, a) => a.indexOf(s) === i);
      srcs.forEach((src) => {
        const at = out.indexOf('src="' + src + '"');
        if (at < 0) return;
        const open = out.lastIndexOf("<img", at);
        const close = out.indexOf(">", at);
        if (open < 0 || close < 0) return;
        const tag = out.slice(open, close + 1);
        if (!/\sheight="\d+"/.test(tag) && !/height\s*:\s*\d+px/i.test(tag)) return;
        const fixed = tag
          .replace(/\sheight="\d+"/, "")
          .replace(/height\s*:\s*\d+px/i, "height: auto");
        out = out.slice(0, open) + fixed + out.slice(close + 1);
      });
    });
    return out;
  } catch {
    return html;
  }
}
